import { useMemo } from 'react';
import { useSprintHandlers } from '../useSprintHandlers';
import useMultiCampaignHandlers from '../useMultiCampaignHandlers';
import { useNestTasks } from '../useNestTasks';
import { useProgressSync } from '../useProgressSync';
import useNestedSprintEvents from '../useNestedSprintEvents';
import useCrossSprintActions from '../useCrossSprintActions';
import useEffectiveHandlers from '../useEffectiveHandlers';
import { useInProgressLockWarning } from '../useInProgressLockWarning';
import { useNestedSprintLoader } from '../../TodoListView/hooks/useNestedSprintLoader';

/**
 * Composes sprint/view handlers: CRUD, nest, progress sync, effective routing, lock warning.
 */
const useComposedViewHandlers = ({
  tasks,
  statuses,
  allTags,
  allSprints,
  localSprint,
  setLocalSprint,
  nested,
  flowchartRef,
  getVisibleTasks,
  onCustomFieldConflict,
  onAddSprint,
  onUpdateSprint,
  onRenameSprint,
  onTrashSprint,
  onSelectSprint,
  loadFullSprint,
  recordAction,
  startBatch,
  endBatch,
  isMultiCampaignMode,
  isTemplate,
  actions,
  modals,
  setup,
  wrappedToolbar,
  campaignViews,
  customViewSettings,
  handleFlowViewVisibilityChangeInternal,
  setShowNestedCampaigns,
  setTableColumnVisibility,
  setTodoElementVisibility,
  setLocalFlowViewVisibility,
  setTodoViewMode,
  setTodoSwimlaneGroupBy,
  setIsCampaignPickerOpen,
  campaignPickerMode,
  setCampaignPickerMode,
  pendingPasteDataRef,
  onRenameView,
  onViewIconChange,
  todoShowNestedItems = true,
}) => {
  const handlers = useSprintHandlers({
    tasks, localSprint, setLocalSprint, nestedSprints: nested.nestedSprints, flowchartRef,
    onAddSprint, onUpdateSprint, onTrashSprint, onRenameSprint, recordAction, startBatch, endBatch, isMultiCampaignMode,
    getVisibleTasks,
    onCustomFieldConflict,
    ensureFlowView: () => {
      const type = campaignViews?.activeCustomView?.type || wrappedToolbar?.currentView;
      if (type === 'board') return;
      wrappedToolbar.setCurrentView('flow');
    },
  });

  const { handleViewRename, handleViewIconChangeLocal, handleCampaignPickerSelect, openCampaignPickerForPaste } = useMultiCampaignHandlers({
    isMultiCampaignMode, onRenameView, onViewIconChange, handlers, tasks, setIsCampaignPickerOpen,
    campaignPickerMode, setCampaignPickerMode, pendingPasteDataRef, flowchartRef,
  });

  const { handleNestTasks, handleCompileMissionsIntoNestedCampaign, handleExplodeNestedCampaign } = useNestTasks({
    tasks, localSprint, setLocalSprint, nestedSprints: nested.nestedSprints, allSprints, onAddSprint, onUpdateSprint,
    recordAction, onTrashSprint, flowchartRef, loadFullSprint,
  });

  // One nest-body loader. Skinny list already has progress %. Astra needs
  // missions for satellites; Todo needs them when nested items are on.
  const viewType =
    campaignViews?.activeCustomView?.type || wrappedToolbar?.currentView;
  const { isLoadingNested } = useNestedSprintLoader({
    tasks,
    allSprints,
    loadFullSprint,
    showNestedItems:
      viewType === 'neural' || (viewType === 'todolist' && todoShowNestedItems),
  });

  const { calculatedProgress } = useProgressSync({
    tasks,
    statuses,
    localSprint,
    setLocalSprint,
    nestedSprints: nested.nestedSprints,
    allSprints,
    onUpdateSprint,
    isMultiCampaignMode,
    isTemplate,
  });

  const { handleTrashNestedSprint } = useNestedSprintEvents({
    tasks, localSprint, setLocalSprint, actions, onTrashSprint,
  });

  const crossSprintActions = useCrossSprintActions({
    tasks, localSprint, allSprints, actions, modals,
    onUpdateSprint, onAddSprint, onSelectSprint, setCurrentView: wrappedToolbar.setCurrentView, flowchartRef,
  });

  const effectiveHandlers = useEffectiveHandlers({
    activeCustomView: campaignViews.activeCustomView,
    customViewSettings,
    handleFiltersChange: setup.handleFiltersChange,
    handleToggleArchived: setup.handleToggleArchived,
    handleToggleActionableOnly: setup.handleToggleActionableOnly,
    setShowNestedCampaigns, setTableColumnVisibility, setTodoElementVisibility,
    setLocalFlowViewVisibility, handleFlowViewVisibilityChangeInternal,
    toolbar: wrappedToolbar,
    handleSortSettingChange: setup.handleSortSettingChange,
    handleCardDisplaySettingsChange: setup.handleCardDisplaySettingsChange,
    handleSwimlaneSettingChange: setup.handleSwimlaneSettingChange,
    setTodoViewMode, setTodoSwimlaneGroupBy,
    setLocalSprint,
  });

  // Shows when adding conditions to a task that was unlocked and has an "in progress" status
  const { inProgressLockWarningModal, createWrappedConditionsChange } = useInProgressLockWarning({
    tasks,
    statuses,
    nestedSprints: nested.nestedSprints,
    allTags,
    onStatusChange: actions.handleTaskStatusChange,
    flowchartRef, // For triggering auto-layout after modal closes
  });

  // Create wrapped actions with the in-progress lock warning check
  const wrappedActions = useMemo(() => ({
    ...actions,
    handleConditionsChange: createWrappedConditionsChange(actions.handleConditionsChange),
  }), [actions, createWrappedConditionsChange]);

  return {
    handlers,
    handleViewRename,
    handleViewIconChangeLocal,
    handleCampaignPickerSelect,
    openCampaignPickerForPaste,
    handleNestTasks,
    handleCompileMissionsIntoNestedCampaign,
    handleExplodeNestedCampaign,
    calculatedProgress,
    handleTrashNestedSprint,
    crossSprintActions,
    effectiveHandlers,
    inProgressLockWarningModal,
    wrappedActions,
    isLoadingNested,
  };
};

export default useComposedViewHandlers;
